import React from 'react';
import Switch from 'react-switch';
import { Link, withRouter } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { apiGet, apiDelete } from 'services';
import { Box, DeleteWarning, DomHelpers, FloatingMenu, Forms, Loading, Paging, SystemText } from 'e4shared';
import CompetencyList from 'components/general/competencyList';
import { hasModule } from 'helpers/profile';
import PageHeader from 'components/general/pageHeader';
import SortFilter from 'components/general/sortFilter';
import NoResults from 'components/general/noResults';
import Question from './components/question';
import ManageQuestion from './components/manageQuestion';

import './knowledgeBank.css';
import { e4Format, e4Formats } from 'helpers/dates';

export const KnowledgeBank = ({ match, history }) => {
    const [data, setData] = React.useState({ records: 0, questions: [] });
    const [loading, setLoading] = React.useState(true);
    const [page, setPage] = React.useState(1);
    const [question, setQuestion] = React.useState({});
    const [manageQuestion, setManageQuestion] = React.useState({});
    const [search, setSearch] = React.useState('');
    const [competency, setCompetency] = React.useState(null);
    const [unanswered, setUnanswered] = React.useState(false);
    const [sort, setSort] = React.useState(2);
    const [showQuestion, setShowQuestion] = React.useState(false);
    const [showDeleteWarning, setShowDeleteWarning] = React.useState(false);
    const [showManageQuestion, setShowManageQuestion] = React.useState(false);

    const { guid, id, administrator } = useSelector(state => state.user.user);
    const { user } = useSelector(state => state.user);
    const leftAsideOpen = useSelector(state => state.ui.leftAsideOpen);

    const loaded = React.useRef();

    const competencies = useSelector(state => state.user.downlineCompetencies);
    const competenciesMapped = competencies ? competencies.map(c => ({ id: c.id, name: `${c.area} - ${c.name}` })) : [];

    if (!hasModule(user, 'knowledge')) history.replace('/');

    const editor = owner => {
        return owner === id || administrator;
    };

    const getQuestions = React.useCallback(() => {
        const gotResponse = response => {
            setData(response);
            setLoading(false);
            resizeHandler();
        };
        apiGet(
            `question-bank/questions?page=${page}&search=${search}&competency=${competency}&unanswered=${unanswered}&sort=${sort}&sortdirection=desc`,
            guid,
            response => gotResponse(response)
        );
    }, [guid, competency, page, search, sort, unanswered]);

    const deleteConfirm = confirm => {
        confirm && apiDelete(`question-bank/question/${question.id}`, null, guid, () => getQuestions(), null);
        setShowDeleteWarning(false);
    };

    // React.useEffect(() => {
    //     apiGet(`question-bank/tags`, guid, response => setPopularTags(response));
    //     apiGet(`question-bank/competencies`, guid, response => setPopularCompetencies(response));
    // }, [guid]);

    const resizeHandler = () => {
        setTimeout(() => {
            DomHelpers.arrangeTiles('question-details');
        }, 500);
    };

    React.useEffect(() => {
        window.addEventListener('resize', resizeHandler);

        return () => {
            window.removeEventListener('resize', resizeHandler);
        };
    }, []); // empty array => run only once

    React.useEffect(() => resizeHandler, [leftAsideOpen, sort, search, competency, unanswered, page, loaded.current]);

    React.useEffect(() => {
        const showId = match.params.id ? parseInt(match.params.id, 10) : 0;

        const gotResponse = response => {
            setQuestion(response);
            setShowQuestion(true);
        };

        showId && apiGet(`question-bank/question/${showId}`, guid, response => gotResponse(response));
    }, [match.params.id]);

    React.useEffect(() => {
        getQuestions();
    }, [guid, page, search, competency, unanswered, sort, getQuestions]);

    const addNewButtons = () => (
        <div className="btn-row">
            <button
                className="btn btn-success"
                data-testid="button-ask-question"
                onClick={() => {
                    setShowManageQuestion(true);
                    setManageQuestion({});
                }}
            >
                <i className="fa fa-question-circle mr5" />
                {SystemText('KnowledgeBank/Buttons/AskQuestion')}
            </button>
            <button
                className="btn btn-success"
                data-testid="button-share-knowledge"
                onClick={() => {
                    setShowManageQuestion(true);
                    setManageQuestion({ own: true });
                }}
            >
                <i className="fa fa-share-alt mr5" />
                {SystemText('KnowledgeBank/Buttons/ShareKnowledge')}
            </button>
        </div>
    );

    const toggle = () => (
        <label className={unanswered ? 'switch-on' : 'switch-off'}>
            <span>{SystemText('KnowledgeBank/Toggle/Answered')}</span>
            <Switch
                className="react-switch"
                onChange={setUnanswered}
                checked={unanswered}
                data-testid="toggle-show-unanswered"
            />
        </label>
    );

    const sortButtons = () => (
        <div className="btn-row">
            <button onClick={() => setSort(2)} className={`btn btn-sm btn-${sort === 2 ? 'primary' : 'default'}`}>
                {SystemText('KnowledgeBank/Buttons/Sort/Date')}
            </button>
            <button onClick={() => setSort(3)} className={`btn btn-sm btn-${sort === 3 ? 'primary' : 'default'}`}>
                {`# ${SystemText('KnowledgeBank/Buttons/Sort/Likes')}`}
            </button>
            <button onClick={() => setSort(4)} className={`btn btn-sm btn-${sort === 4 ? 'primary' : 'default'}`}>
                {`# ${SystemText('KnowledgeBank/Buttons/Sort/Answers')}`}
            </button>
        </div>
    );

    const filterFields = () => (
        <div className="row">
            <div className="col-sm-12 col-md-6">
                <Forms.Text
                    id="search"
                    label={SystemText('KnowledgeBank/Fields/Search/Label')}
                    value={search}
                    onChange={value => setSearch(value)}
                    icon="fa-search"
                />
            </div>
            <div className="col-sm-12 col-md-6">
                <Forms.Select
                    id="competency_Id"
                    label={SystemText('KnowledgeBank/Fields/Competency/Label')}
                    data={competenciesMapped}
                    value={competency}
                    onChange={value => setCompetency(value)}
                />
            </div>
        </div>
    );

    return (
        <div id="knowledgeBank" ref={loaded}>
            <PageHeader title={SystemText('KnowledgeBank/Header/Title')} extra={addNewButtons} />
            <section id="knowledge-bank" className="content mtm10 knowledge-bank" data-testid="knowledgebank-content">
                <SortFilter sortButtons={sortButtons} toggle={toggle} filterFields={filterFields} />
                <div className="row">
                    <div className="col-md-12">
                        <div className="row">
                            {loading ? (
                                <Loading />
                            ) : data.questions?.length ? (
                                data.questions.map((q, i) => {
                                    const approved = q?.answers?.some(a => a.approved);
                                    return (
                                        <div
                                            className="col-lg-3 col-md-4 col-sm-6 col-xs-12"
                                            data-testid={`question-${q.id}`}
                                            key={`question-${q.id}`}
                                        >
                                            {q.sticky && (
                                                <div
                                                    className="featured-banner"
                                                    style={{ padding: '5px 10px', left: '-15px' }}
                                                >
                                                    {SystemText('KnowledgeBank/Featured')}
                                                </div>
                                            )}
                                            {editor(q.owner_Id) && (
                                                <FloatingMenu>
                                                    <li
                                                        onClick={() => {
                                                            setManageQuestion({ ...q });
                                                            setShowManageQuestion(q);
                                                        }}
                                                        title={SystemText('KnowledgeBank/Buttons/Edit/Title')}
                                                    >
                                                        <i className="fa fa-pencil mr10" />
                                                        {SystemText('KnowledgeBank/Buttons/Edit/Title')}
                                                    </li>
                                                    <li
                                                        onClick={() => {
                                                            setQuestion(q);
                                                            setShowDeleteWarning(true);
                                                        }}
                                                        title={SystemText('KnowledgeBank/Buttons/Delete/Title')}
                                                    >
                                                        <i className="fa fa-trash mr10" />
                                                        {SystemText('KnowledgeBank/Buttons/Delete/Title')}
                                                    </li>
                                                </FloatingMenu>
                                            )}
                                            <Box className={q.sticky ? 'box-warning post-card' : 'post-card'}>
                                                <div className="question-details">
                                                    <div
                                                        style={{ minWidth: '100%', minHeight: '100px' }}
                                                        className={`text-center mb10 question`}
                                                    >
                                                        <h1 className={`${approved ? 'label label-success' : ''}`}>
                                                            {q?.answers?.length}
                                                        </h1>
                                                        <p className={`text-sm`}>
                                                            {q?.answerCount === 1
                                                                ? SystemText('KnowledgeBank/Answer').toUpperCase()
                                                                : SystemText('KnowledgeBank/Answer/Plural').toUpperCase()}
                                                        </p>
                                                    </div>
                                                    <div>
                                                        <button
                                                            className="btn btn-link flex-grow btn-lg mb10 mr50"
                                                            data-testid={`button-view-question-${q.id}`}
                                                            onClick={() => {
                                                                setShowQuestion(true);
                                                                setQuestion(q);
                                                            }}
                                                        >
                                                            {q.name}
                                                        </button>
                                                        <div className="mb5">
                                                            <span className="">{SystemText('KnowledgeBank/By')} </span>
                                                            <Link to={`/search/${q.owner_Full_Name}`}>
                                                                {q.owner_Full_Name}
                                                            </Link>
                                                        </div>
                                                        <div className="mb5">
                                                            {e4Format(q.created, e4Formats.long, true)}
                                                        </div>
                                                        {q.tags && q.tags.length > 0 && (
                                                            <div className="mb5">
                                                                {q.tags.map((t, i) => {
                                                                    return (
                                                                        <span key={`tag-${q.id}-${i}`}>
                                                                            <button
                                                                                className="btn btn-link"
                                                                                onClick={() => {
                                                                                    setSearch('#' + t);
                                                                                    setPage(1);
                                                                                }}
                                                                            >
                                                                                #{t}
                                                                            </button>
                                                                            {i < q.tags.length - 1 && (
                                                                                <span className="mr5 ml5">|</span>
                                                                            )}
                                                                        </span>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                        {q.competency_Ids && q.competency_Ids.length > 0 && (
                                                            <div className="">
                                                                <CompetencyList competencies={q.competencies} />
                                                            </div>
                                                        )}
                                                        <div className="flex flex-center">
                                                            <span className="mr10">
                                                                <i
                                                                    className="fa fa-thumbs-up"
                                                                    title={
                                                                        q?.likes === 1
                                                                            ? SystemText('KnowledgeBank/Like')
                                                                            : SystemText(
                                                                                  'KnowledgeBank/Like/Plural',
                                                                                  q?.likes || 0
                                                                              )
                                                                    }
                                                                />{' '}
                                                                {q.likes}
                                                            </span>
                                                            {q?.answers?.some(a => a.assets) && (
                                                                <span>
                                                                    <i
                                                                        title={SystemText(
                                                                            'KnowledgeBank/Buttons/Attachment/Title'
                                                                        )}
                                                                        className="fa fa-paperclip mr5"
                                                                    />
                                                                    Attachment
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </Box>
                                        </div>
                                    );
                                })
                            ) : search || competency || unanswered ? (
                                <NoResults
                                    title={SystemText('KnowledgeBank/NoResults/Title')}
                                    message={SystemText('KnowledgeBank/NoResults')}
                                />
                            ) : (
                                <NoResults
                                    title={SystemText('KnowledgeBank/NoQuestions/Title')}
                                    message={SystemText('KnowledgeBank/NoQuestions')}
                                />
                            )}
                        </div>
                        {data.questions && data.questions.length > 0 && (
                            <div className="text-center mt10 mb10">
                                <Paging
                                    page={page}
                                    pageSize={10}
                                    recordCount={data.records}
                                    goToPage={page => setPage(page)}
                                />
                            </div>
                        )}
                    </div>
                    {/* <div className="hidden-sm hidden-md col-lg-3">
                        <Box className="box-success">
                            <div className="mt10 mb5">
                                <b>{SystemText('KnowledgeBank/RecentCompetencies')}</b>
                            </div>
                            {!popularCompetencies || (popularCompetencies.length === 0 && <div>-</div>)}
                            {popularCompetencies &&
                                popularCompetencies.map(cmp => (
                                    <div className="flex" key={`comp-${cmp.id}`}>
                                        <i className="fa fa-caret-right mr5 mt2" />
                                        <button className="flex-grow btn btn-link" onClick={() => setCompetency(cmp.id)}>
                                            {cmp.area} - {cmp.name} ({cmp.count})
                                        </button>
                                    </div>
                                ))}
                            <div className="mt10 mb5">
                                <b>{SystemText('KnowledgeBank/RecentTags')}</b>
                            </div>
                            {!popularTags || (popularTags.length === 0 && <div>-</div>)}
                            {popularTags &&
                                popularTags.map(tag => (
                                    <div className="flex" key={`ptag-${tag.tag}`}>
                                        <i className="fa fa-caret-right mr5 mt2" />
                                        <button className="flex-grow btn btn-link" onClick={() => setSearch('#' + tag.tag)}>
                                            {tag.tag} ({tag.count})
                                        </button>
                                    </div>
                                ))}
                        </Box>
                    </div> */}
                </div>
            </section>
            <Question
                question={question}
                show={showQuestion}
                onClose={() => {
                    setQuestion({});
                    setShowQuestion(false);
                }}
                onReload={() => getQuestions()}
            />
            <ManageQuestion
                question={manageQuestion}
                show={showManageQuestion}
                onClose={() => {
                    setManageQuestion({});
                    setShowManageQuestion(false);
                }}
                onReload={() => getQuestions()}
            />
            <DeleteWarning
                id="deleteWarning"
                message={SystemText('KnowledgeBank/Warning/Delete')}
                show={showDeleteWarning}
                close={confirm => deleteConfirm(confirm)}
            />
        </div>
    );
};

export default withRouter(KnowledgeBank);
